import { defineMessages, locales, translator, type Locale } from "./i18n/index.ts";

/*
 * « Explique-moi » : un mot ou deux sont cherchés dans le dictionnaire ; un passage plus long peut être
 * expliqué dans le chat du livre. Repris de Studio (dépôt ai, `nextjs/src/reader/explain.ts`).
 */

// Envoyé au chat comme la question du lecteur : l'assistant répond dans sa langue.
const messages = defineMessages({ question: "Explique ce passage :" }, { question: "Explain this passage:" });

const SHORT_LOOKUP_WORDS = 2;

export const cleanSelection = (text: string) => text.replace(/\s+/g, " ").trim();

export const wordCount = (text: string) => (cleanSelection(text) ? cleanSelection(text).split(" ").length : 0);

export const isShortLookup = (text: string) => wordCount(text) <= SHORT_LOOKUP_WORDS;

/** La question et le passage d'un message fait par `explainQuestion` (dans l'une ou l'autre langue), pour les afficher à part. */
export function parseExplainQuestion(content: string): { question: string; passage: string } | null {
  for (const locale of locales) {
    const question = translator(messages, locale)("question");
    const match = new RegExp(`^${question} "([\\s\\S]*)"$`).exec(content);
    if (match) return { question, passage: match[1]! };
  }
  return null;
}

/** La question posée au livre quand un passage entier est sélectionné, dans la langue de l'interface. */
export const explainQuestion = (text: string, locale: Locale = "fr") => `${translator(messages, locale)("question")} "${cleanSelection(text)}"`;

export interface Definition {
  title: string;
  extract: string;
  /** The article the definition comes from. */
  url: string;
}

const strip = (term: string) => cleanSelection(term).replace(/^[\s"'«»“”‘’.,;:!?()\[\]]+|[\s"'«»“”‘’.,;:!?()\[\]]+$/g, "");

export const wikipediaUrl = (term: string, language = "fr") =>
  `https://${language}.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(strip(term))}`;

export const googleUrl = (term: string) => `https://www.google.com/search?q=${encodeURIComponent(`${strip(term)} définition`)}`;

const OTHER_LANGUAGE = /^(Anglais|Allemand|Espagnol|Italien|Latin|Néerlandais|Portugais|Occitan|Ancien français|Moyen français|Breton|Catalan|Roumain|Polonais|Russe|Suédois|Japonais|Chinois|Arabe|Grec ancien|Interlingua|Espéranto|Corse|Picard|Wallon|Gallo|Normand|Francoprovençal|Créole .+|Conventions internationales)$/;
/** The headings of a word class: "Nom commun", "Adjectif 2", "Locution adverbiale", "Forme de verbe"… */
const WORD_CLASS = /^(Nom commun|Nom propre|Adjectif(?: (?:numéral|possessif|démonstratif|indéfini|interrogatif|exclamatif|relatif))?|Adverbe(?: \S+)?|Verbe|Pronom(?: \S+)?|Préposition|Conjonction(?: de coordination)?|Interjection|Article(?: \S+)?|Onomatopée|Locution .+|Forme (?:de |d[’'])(?:nom commun|adjectif|verbe|adverbe|pronom|locution .+|nom propre|article .+))(?: \d+)?$/;
/** The other headings of an entry, whose content is not a definition. */
const OTHER_SECTION = /^(Étymologie|Prononciation|Anagrammes|Références|Voir aussi|Dérivés|Synonymes|Quasi-synonymes|Antonymes|Traductions|Notes?|Apparentés.*|Variantes.*|Vocabulaire.*|Hyperonymes|Hyponymes|Holonymes|Méronymes|Homophones|Paronymes|Citations|Proverbes.*|Expressions|Faux-amis|Fréquence|Composés|Compositions|Diminutifs|Augmentatifs|Abréviations|Gentilés.*|Phrases.*|Dérivés dans d[’']autres langues|Attestations historiques|Taxons|Erreurs fréquentes|Forme(?:s)? (?:alternative|dérivée).*|Sources|Bibliographie|Dérivés autres langues|Typographie|Hyponymes|Troponymes|Anciennes orthographes|Abréviations|Transcriptions dans diverses écritures|Conjugaison)(?: \d+)?$/;
/** Words of the head line kept after the word class ("Nom" + "féminin", "Verbe" + "transitif"). */
const QUALIFIERS = /\b(masculin et féminin identiques|masculin et féminin|masculin|féminin|invariable|transitif|intransitif|pronominal|impersonnel)\b/g;
const MAX_CLASSES = 2;
const MAX_MEANINGS = 4;

const withoutPronunciation = (line: string) => line.replace(/\\[^\\]*\\/g, "").replace(/\s+/g, " ").trim();
/** An example of use, with its source: "Je n’ai aucune antipathie pour les chats… — (Poe, trad. Baudelaire)". */
const isExample = (line: string) => /(^|\s)—\s*\(/.test(line) || /^[—–-]\s/.test(line) || /^«/.test(line);
const isNoise = (line: string) => /^→|écouter «|\[Prononciation \?\]|^Note\s?:|^Modifier/.test(line);

interface WordClass {
  heading: string;
  head: string;
  meanings: string[];
}

/** The word classes of the French entry of a Wiktionary page in plain text, with their meanings. */
function wordClasses(extract: string): WordClass[] {
  const lines = extract.split("\n").map((line) => line.trim()).filter(Boolean);
  const start = lines.findIndex((line) => line === "Français");
  const classes: WordClass[] = [];
  let current: WordClass | null = null;
  for (const line of start >= 0 ? lines.slice(start + 1) : lines.slice(1)) {
    if (OTHER_LANGUAGE.test(line)) break;
    if (WORD_CLASS.test(line)) {
      current = { heading: line.replace(/ \d+$/, ""), head: "", meanings: [] };
      classes.push(current);
      continue;
    }
    if (OTHER_SECTION.test(line)) {
      current = null;
      continue;
    }
    if (!current) continue;
    const text = withoutPronunciation(line);
    if (!text || isNoise(text)) continue;
    // The first line of a word class is its head line: "antipathie \\ɑ̃.ti.pa.ti\\ féminin".
    if (!current.head) current.head = text;
    else if (!isExample(text)) current.meanings.push(text);
  }
  for (const wordClass of classes) {
    // "chétive \\ke.tiv\\ féminin singulier de chétif.": a form whose head line says what it is a form of.
    if (wordClass.heading.startsWith("Forme ") && wordClass.meanings.length === 0 && /\bde\s/.test(wordClass.head)) {
      wordClass.meanings.push(wordClass.head.replace(/^\S+\s+/, ""));
    }
  }
  return classes.filter((wordClass) => wordClass.meanings.length > 0);
}

/** "Nom féminin", "Adjectif", "Verbe transitif": the word class and the qualifiers of its head line. */
function classLabel({ heading, head }: WordClass): string {
  const name = heading === "Nom commun" ? "Nom" : heading;
  const qualifiers = [...new Set(head.match(QUALIFIERS) ?? [])];
  return [name, ...qualifiers].join(" ");
}

/**
 * The definitions of the French entry of a Wiktionary page: "Nom féminin" then its meanings, numbered when there
 * are several. The pronunciation, etymology, examples, anagrams and translations are left out.
 */
export function parseWiktionary(extract: string): string | null {
  const blocks = wordClasses(extract)
    .slice(0, MAX_CLASSES)
    .map((wordClass) => {
      const meanings = wordClass.meanings.slice(0, MAX_MEANINGS);
      const list = meanings.length > 1 ? meanings.map((meaning, index) => `${index + 1}. ${meaning}`) : meanings;
      return [classLabel(wordClass), ...list].join("\n");
    });
  const result = blocks.join("\n\n");
  return result.length > 12 ? (result.length > 900 ? `${result.slice(0, 900)}…` : result) : null;
}

/** Does the entry only name another form of a word ("Pluriel de antipathie.")? Then that word is the one to show. */
const onlyInflected = (extract: string) => {
  const classes = wordClasses(extract);
  return classes.length > 0 && classes.every((wordClass) => wordClass.heading.startsWith("Forme "));
};

/** The base form named by a definition such as "féminin singulier de chétif", to look up as well. */
export function baseForm(definition: string): string | null {
  const match = /(?:féminin|masculin|pluriel|singulier|participe|personne|subjonctif|indicatif|impératif|conditionnel|infinitif|forme)[^.\n]*?\bde\s+(?:l[’']\s*)?([\p{L}-]+)\s*\.?\s*$/imu.exec(definition);
  return match ? match[1]! : null;
}

async function wiktionary(word: string, signal?: AbortSignal): Promise<(Definition & { inflected: boolean }) | null> {
  const url =
    "https://fr.wiktionary.org/w/api.php?action=query&prop=extracts&explaintext=1&exsectionformat=plain&redirects=1&format=json&formatversion=2&origin=*&titles=" +
    encodeURIComponent(word);
  const response = await fetch(url, { signal });
  if (!response.ok) return null;
  const data = (await response.json()) as { query?: { pages?: { title?: string; extract?: string; missing?: boolean }[] } };
  const page = data.query?.pages?.[0];
  if (!page || page.missing || !page.extract) return null;
  const extract = parseWiktionary(page.extract);
  const title = page.title ?? word;
  return extract ? { title, extract, url: `https://fr.wiktionary.org/wiki/${encodeURIComponent(title)}`, inflected: onlyInflected(page.extract) } : null;
}

async function wikipedia(word: string, signal?: AbortSignal): Promise<Definition | null> {
  const response = await fetch(`https://fr.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(word)}?redirect=true`, { signal });
  if (!response.ok) return null;
  const data = (await response.json()) as { title?: string; extract?: string; type?: string; content_urls?: { desktop?: { page?: string } } };
  if (data.type === "disambiguation" || !data.extract) return null;
  return { title: data.title ?? word, extract: data.extract, url: data.content_urls?.desktop?.page ?? `https://fr.wikipedia.org/wiki/${encodeURIComponent(word)}` };
}

/**
 * The definition of a word or two. A web page cannot ask the device's dictionary, so the French Wiktionary
 * (the dictionary of Wikimedia) is asked first, as typed then in lower case ("Chétive" at the start of a sentence),
 * with the base form of an inflected word ("chétive": féminin de chétif) added; then Wikipedia for names and
 * notions. `null` when none of them knows it.
 */
export async function lookupDefinition(term: string, signal?: AbortSignal): Promise<Definition | null> {
  const word = strip(term);
  if (!word) return null;
  const attempt = async <T>(lookup: () => Promise<T | null>) => {
    try {
      return await lookup();
    } catch (error) {
      if (signal?.aborted) throw error;
      return null;
    }
  };
  const spellings = [...new Set([word, word.toLocaleLowerCase("fr")])];
  for (const spelling of spellings) {
    const found = await attempt(() => wiktionary(spelling, signal));
    if (!found) continue;
    const base = baseForm(found.extract);
    const root = base && base.toLowerCase() !== spelling.toLowerCase() ? await attempt(() => wiktionary(base, signal)) : null;
    if (!root) return found;
    // "antipathies": the definition of "antipathie" straight away, the plural adds nothing.
    if (found.inflected) return { title: root.title, extract: root.extract, url: root.url };
    return { ...found, extract: `${found.extract}\n\n${root.title} : ${root.extract}` };
  }
  for (const spelling of spellings) {
    const found = await attempt(() => wikipedia(spelling, signal));
    if (found) return found;
  }
  return null;
}
